/**
 * Commission API surface (H4).
 *
 * Money is a string end to end, the same as the ledger and invoicing: a NUMERIC that has
 * been through a JavaScript double is not a figure anyone should be paid on. Nothing here
 * adds two amounts together — every total on these screens is one the server computed.
 *
 * A period is a month, written `YYYY-MM`. The API takes it that way and answers with the
 * half-open bounds it resolved to, so the screen never has to work out when a month ends.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export type RuleKind = 'flat' | 'tiered'

export type AccrualStatus = 'pending' | 'approved' | 'cancelled'

/** When commission is earned. Mirrors `COMMISSION_ACCRUALS` in the API's `company/models.py`. */
export type AccrualPolicy = 'issue' | 'collection'

export const STATUS_LABELS: Record<AccrualStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  cancelled: 'Cancelled',
}

export interface Tier {
  min_amount: string
  rate_pct: string
}

export interface CommissionRule {
  id: string
  name: string
  kind: RuleKind
  /** Null applies the rule to every rep; null category applies it to every category. */
  user_id: string | null
  category_id: string | null
  rate_pct: string | null
  tiers: Tier[]
  valid_from: string
  valid_to: string | null
  is_active: boolean
  sequence: number
  created_at: string
  updated_at: string
}

export interface RuleInput {
  name: string
  kind: RuleKind
  user_id?: string | null
  category_id?: string | null
  rate_pct?: string | null
  tiers?: Tier[]
  valid_from: string
  valid_to?: string | null
  is_active?: boolean
  sequence?: number
}

/** One line of the frozen calculation, as the accrual recorded it. */
export interface BreakdownEntry {
  rule_id: string | null
  rule_name: string
  basis_amount: string
  rate_pct: string
  amount: string
}

export interface Accrual {
  id: string
  user_id: string
  /**
   * Exactly one of these two is set. An invoice earned its rep; a counter sale earned the
   * manager of the shop it was rung at, and a POS sale is not an invoice (ADR-015).
   */
  invoice_id: string | null
  pos_transaction_id: string | null
  /** Null for a walk-in, which is most counter sales. */
  customer_id: string | null
  location_id: string | null
  period_start: string
  basis_amount: string
  amount: string
  rate_pct: string | null
  breakdown: BreakdownEntry[]
  status: AccrualStatus
  accrual_policy: AccrualPolicy
  approved_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  created_at: string
  invoice_number: string | null
  /** The counter equivalent, frozen onto the accrual rather than looked up. */
  receipt_number: string | null
  customer_name: string | null
  user_name: string | null
}

export interface Statement {
  user_id: string
  user_name: string | null
  period_start: string
  period_end: string
  basis_amount: string
  /** Earned but not yet payable — under a collection policy, the customer has not paid. */
  pending_amount: string
  approved_amount: string
  cancelled_amount: string
  invoice_count: number
  lines: Accrual[]
}

export interface PayrollLine {
  user_id: string
  employee_id: string | null
  employee_code: string | null
  name: string | null
  /** Null for a rep with no HR record. They still earn commission. */
  base_salary: string | null
  commission_amount: string
  gross_amount: string
}

export interface PayrollPreview {
  period_start: string
  period_end: string
  lines: PayrollLine[]
  total_base: string
  total_commission: string
  total_gross: string
}

export interface RecomputeDifference {
  accrual_id: string
  invoice_id: string
  invoice_number: string | null
  user_id: string
  stored_amount: string
  recomputed_amount: string
  difference: string
}

export interface RecomputeResult {
  period_start: string
  period_end: string
  accruals_checked: number
  matched: number
  applied: boolean
  differences: RecomputeDifference[]
}

const BASE = `${API_PREFIX}/commissions`

export const listRules = (params: { page?: number; active_only?: boolean } = {}) => {
  const query = new URLSearchParams()
  if (params.page && params.page > 1) query.set('page', String(params.page))
  if (params.active_only) query.set('active_only', 'true')
  const qs = query.toString()
  return apiFetch<Page<CommissionRule>>(`${BASE}/rules${qs ? `?${qs}` : ''}`)
}

export const createRule = (input: RuleInput) =>
  apiGet<CommissionRule>(`${BASE}/rules`, { method: 'POST', body: JSON.stringify(input) })

/** A full replacement, not a patch: kind, rate and tiers only mean anything together. */
export const updateRule = (id: string, input: RuleInput) =>
  apiGet<CommissionRule>(`${BASE}/rules/${id}`, { method: 'PUT', body: JSON.stringify(input) })

export const deleteRule = (id: string) =>
  apiFetch<void>(`${BASE}/rules/${id}`, { method: 'DELETE' })

/**
 * Individual accruals, scoped to the caller.
 *
 * The statement screen reads this to build its rep picker: the people it lists are exactly
 * the ones this user may see who earned something in the period, so the picker cannot
 * offer a name whose statement comes back empty.
 */
export const listAccruals = (params: {
  period: string
  page_size?: number
  location_id?: string
}) => {
  const query = new URLSearchParams({
    period: params.period,
    page_size: String(params.page_size ?? 200),
  })
  if (params.location_id) query.set('location_id', params.location_id)
  return apiFetch<Page<Accrual>>(`${BASE}/accruals?${query.toString()}`)
}

/** Omit `userId` to read your own month — the common case, and the server's default. */
export const getStatement = (period: string, userId?: string, locationId?: string) => {
  const query = new URLSearchParams({ period })
  if (userId) query.set('user_id', userId)
  if (locationId) query.set('location_id', locationId)
  return apiGet<Statement>(`${BASE}/statement?${query.toString()}`)
}

export const exportAccruals = (
  format: ExportFormat,
  params: { period?: string; user_id?: string; status?: string; location_id?: string } = {},
) => {
  const query = new URLSearchParams({ fmt: format })
  if (params.period) query.set('period', params.period)
  if (params.user_id) query.set('user_id', params.user_id)
  if (params.status) query.set('status', params.status)
  if (params.location_id) query.set('location_id', params.location_id)
  return apiBlob(`${BASE}/accruals/export?${query}`)
}

export const getPayrollPreview = (period: string, locationId?: string) => {
  const query = new URLSearchParams({ period })
  if (locationId) query.set('location_id', locationId)
  return apiGet<PayrollPreview>(`${BASE}/payroll-preview?${query.toString()}`)
}

/**
 * Re-run a month against today's rules.
 *
 * `apply` defaults to false and the screen keeps it that way until somebody asks: a
 * difference means a rate was edited after the period was accrued, and whether last month
 * is restated is a decision for finance rather than a side effect of opening a report.
 */
export const recompute = (period: string, apply = false) =>
  apiGet<RecomputeResult>(`${BASE}/recompute?period=${period}&apply=${apply}`, { method: 'POST' })

/** Rupees, en-IN grouping. Same formatter every other money screen prints through. */
export { formatMoney } from '@/lib/money'

/** `YYYY-MM` for the month containing `on`. The only period format the API accepts. */
export function currentPeriod(on = new Date()): string {
  return `${on.getFullYear()}-${String(on.getMonth() + 1).padStart(2, '0')}`
}

/** "August 2026" from `2026-08`, for a heading. */
export function periodLabel(period: string): string {
  const [year, month] = period.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * How a rate card reads in one line: "3%", or the tiers as thresholds.
 *
 * Tiers are slabs — the whole basis earns the highest rate it reaches — so they are shown
 * as "from X" rather than as ranges, which would imply the marginal reading.
 */
export function describeRate(rule: CommissionRule, format: (v: string) => string): string {
  if (rule.kind === 'flat') return rule.rate_pct === null ? '—' : `${Number(rule.rate_pct)}%`
  if (rule.tiers.length === 0) return '—'
  return rule.tiers
    .map((tier) => `${Number(tier.rate_pct)}% from ${format(tier.min_amount)}`)
    .join(', ')
}
